import { useDispatch } from 'react-redux';
import logger from './logger';
import {
  setHistoryMsg,
  setInterruptMsg,
  updateAITalkState,
  updateAIThinkState,
} from '@/store/storeTTS/slices/room';
import RtcClient from '../../libTTS/RtcClient';
import Utils from './utils';
// 添加事件派发器，用于与聊天系统通信
import { EventEmitter } from 'events';

// 创建全局事件发射器
export const ttsEventEmitter = new EventEmitter();

export type AnyRecord = Record<string, any>;

export enum MESSAGE_TYPE {
  BRIEF = 'conv',
  SUBTITLE = 'subv',
  FUNCTION_CALL = 'tool',
}

export enum AGENT_BRIEF {
  UNKNOWN,
  LISTENING,
  THINKING,
  SPEAKING,
  INTERRUPTED,
  FINISHED,
}

/**
 * @brief 指令类型
 */
export enum COMMAND {
  /**
   * @brief 打断指令
   */
  INTERRUPT = 'interrupt',
  /**
   * @brief 发送外部文本驱动 TTS
   */
  EXTERNAL_TEXT_TO_SPEECH = 'ExternalTextToSpeech',
  /**
   * @brief 发送外部文本驱动 LLM
   */
  EXTERNAL_TEXT_TO_LLM = 'ExternalTextToLLM',
}
/**
 * @brief 打断的类型
 */
export enum INTERRUPT_PRIORITY {
  /**
   * @brief 占位
   */
  NONE,
  /**
   * @brief 高优先级。传入信息直接打断交互，进行处理。
   */
  HIGH,
  /**
   * @brief 中优先级。等待当前交互结束后，进行处理。
   */
  MEDIUM,
  /**
   * @brief 低优先级。如当前正在发生交互，直接丢弃 Message 传入的信息。
   */
  LOW,
}
export const MessageTypeCode = {
  [MESSAGE_TYPE.SUBTITLE]: 1,
  [MESSAGE_TYPE.FUNCTION_CALL]: 2,
  [MESSAGE_TYPE.BRIEF]: 3,
};
export const useMessageHandler = () => {
  const dispatch = useDispatch();

  const maps = {
    /**
     * @brief 接收状态变化信息
     * @note https://www.volcengine.com/docs/6348/1415216?s=g
     * //1.THINKING思考 //2.SPEAKING说话 //3.FINISHED结束 //4.INTERRUPTED中断
     */
    [MESSAGE_TYPE.BRIEF]: (parsed: AnyRecord) => {
      const { Stage } = parsed || {};
      const { Code, Description } = Stage || {};
      logger.debug(Code, Description, '状态变化信息');
      switch (Code) {
        case AGENT_BRIEF.LISTENING:
          logger.debug(Code, Description, '状态变化信息LISTENING正在听');
          // 派发事件到聊天系统
          ttsEventEmitter.emit('aiStatusChange', { status: 'listening' });
          break;
        case AGENT_BRIEF.THINKING:
          logger.debug(Code, Description, '状态变化信息THINKING思考');
          dispatch(updateAIThinkState({ isAIThinking: true }));
          // 派发事件到聊天系统
          ttsEventEmitter.emit('aiStatusChange', { status: 'thinking' });
          break;
        case AGENT_BRIEF.SPEAKING:
          logger.debug(Code, Description, '状态变化信息SPEAKING说话');
          dispatch(updateAITalkState({ isAITalking: true }));
          // 派发事件到聊天系统
          ttsEventEmitter.emit('aiStatusChange', { status: 'speaking' });
          break;
        case AGENT_BRIEF.FINISHED:
          logger.debug(Code, Description, '状态变化信息FINISHED结束');
          dispatch(updateAITalkState({ isAITalking: false }));
          // 派发事件到聊天系统
          ttsEventEmitter.emit('aiStatusChange', { status: 'finished' });
          break;
        case AGENT_BRIEF.INTERRUPTED:
          logger.debug(Code, Description, '状态变化信息INTERRUPTED中断');
          dispatch(setInterruptMsg());
          // 派发事件到聊天系统
          ttsEventEmitter.emit('aiStatusChange', { status: 'interrupted' });
          break;
        default:
          break;
      }
    },
    /**
     * @brief 字幕
     * @note https://www.volcengine.com/docs/6348/1337284?s=g
     */
    [MESSAGE_TYPE.SUBTITLE]: (parsed: AnyRecord) => {
      const data = parsed.data?.[0] || {};
      /** debounce 记录用户输入文字 */
      if (data) {
        const { text: msg, definite, userId: user, paragraph,roundId,timestamp } = data;
        logger.debug('handleRoomBinaryMessageReceived', data);
        if ((window as any)._debug_mode) {
          dispatch(setHistoryMsg({ text: msg, user, paragraph, definite }));
        } else {
          const isAudioEnable = RtcClient.getAudioBotEnabled();
          if (isAudioEnable) {
            dispatch(setHistoryMsg({ text: msg, user, paragraph, definite }));
            // 派发事件到聊天系统，包含definite和paragraph参数
            // 根据user判断角色：RobotMan_为assistant(AI)，其他为local(用户)
            const role = user === 'RobotMan_' ? 'assistant' : 'local';
            ttsEventEmitter.emit('ttsMessage', { 
              text: msg, 
              user, 
              paragraph, 
              definite,
              role: role,
              roundId: roundId,
              timestamp
            });
          }
        }
      }
    },
  };

  return {
    parser: (buffer: ArrayBuffer) => {
      try {
        const { type, value } = Utils.tlv2String(buffer);
        maps[type as MESSAGE_TYPE]?.(JSON.parse(value));
      } catch (e) {
        logger.debug('parse error', e);
      }
    },
  };
};